import React from "react";
import _ from "lodash";
import moment from "moment-timezone";
import numeral from "numeral";
import LoadingUI from "components/common/LoadingUI";
import ModalWrapper from "components/common/ModalWrapper";
import HighchartWrapper from "components/common/HighchartWrapper";
import { DATE_RANGE_TYPE } from "components/playground/home-dashboard-v2/DateHelper";
import DateRangePicker from "components/common/DateRangePicker";
import SwitchTabs from "components/common/SwitchTabs";

import { AnatomyAPI, NetworkAPI } from "apis";
import { addChartBgSection, getCurrencySymbol } from "helpers/Utils";
import { normalizeRevenue } from "helpers/Chart";
import { Z_INDEX } from "constants/UI";

// metricKey:
// pscore_trend, dscore_trend, uprkey_trend
// ivt_trend, algomscore_trend, cost_trend
const METRIC = {
  REQUEST_TREND: "req_trend",
  PSCORE_TREND: "pscore_trend",
  DSCORE_TREND: "dscore_trend",
  UPRKEY_TREND: "uprkey_trend",
  ALGOM_SCORE_TREND: "algomscore_trend",
  IVT_TREND: "ivt_trend",
  COST_TREND: "cost_trend",
  ECPM_RATIO: "ecpm_ratio",
  STR_RATIO: "str_ratio",
  BO_IVT_RATIO: "ivt_b_o_trend",
};

const METRIC_KEY = {
  [METRIC.PSCORE_TREND]: "pscore",
  [METRIC.DSCORE_TREND]: "dscore",
  [METRIC.UPRKEY_TREND]: "uprkey",
  [METRIC.IVT_TREND]: "ivt_ratio",
  [METRIC.ALGOM_SCORE_TREND]: "algomscore",
  [METRIC.COST_TREND]: "costRatio",
  [METRIC.ECPM_RATIO]: "ecpm_ratio",
  [METRIC.STR_RATIO]: "str_ratio",
  [METRIC.BO_IVT_RATIO]: "boIvtRatio",
  [METRIC.REQUEST_TREND]: "request",
};

const METRIC_TITLE = {
  [METRIC.PSCORE_TREND]: { title: "PScore", subtitle: "Req" },
  [METRIC.DSCORE_TREND]: { title: "DScore", subtitle: "Req" },
  [METRIC.UPRKEY_TREND]: { title: "UPR Key", subtitle: "Req" },
  [METRIC.ALGOM_SCORE_TREND]: { title: "MScore Algo", subtitle: "H Rev" },
  [METRIC.IVT_TREND]: { title: "IVT Ratio", subtitle: "" },
  [METRIC.COST_TREND]: { title: "Cost Ratio", subtitle: "" },
  [METRIC.ECPM_RATIO]: { title: "b vs o eCPM", subtitle: "" },
  [METRIC.STR_RATIO]: { title: "b vs o STR", subtitle: "" },
  [METRIC.BO_IVT_RATIO]: { title: "b vs o IVT", subtitle: "" },
  [METRIC.REQUEST_TREND]: { title: "Request", subtitle: "" },
};

function createBOTooltipHtml(data) {
  return `<table><tr>
        <td></td>
        <td style='text-align: center;'>b</td>
        <td style='text-align: center;'>o</td>
      </tr>${data
        .map(({ label = "", valList = [], format = "0,0.00", symbol = "" }) => {
          return `<tr>
          <td>${label}</td>
          ${valList
            .map(
              (value) =>
                `<td style='text-align: right; padding-left: 6px;'>${symbol}${numeral(
                  value,
                ).format(format)}</td>`,
            )
            .join("")}
        </tr>`;
        })
        .join("")}</table>`;
}

const HELPER_LINE = 100;

class UnitMetricTrendModal extends React.Component {
  constructor(props) {
    super(props);

    const today = moment().format("YYYY-MM-DD");

    this.state = {
      errmsg: "",
      isLoading: false,
      startDate: moment(today).subtract(30, "d").format("YYYY-MM-DD"),
      endDate: today,
      metricType: this.props.metricType,

      twdConvertRate: 1,
      currency: "",
      selectedCurrency: "",

      originData: null,
      options: null,
    };

    this.handleDateRangeFilterChanged =
      this.handleDateRangeFilterChanged.bind(this);
    this.handleMetricTypeChange = this.handleMetricTypeChange.bind(this);
    this.handleCurrencyChange = this.handleCurrencyChange.bind(this);
    this.handleChartOption = this.handleChartOption.bind(this);
    this.handleGetData = this.handleGetData.bind(this);
  }

  async componentDidMount() {
    const { isInTwd } = this.props;
    try {
      this.setState({ isLoading: true });
      const { twdConvertRate, currency } =
        await NetworkAPI.getLatestTwdConvertRate({
          networkId: this.props.networkId,
        });

      let selectedCurrency = isInTwd ? "TWD" : currency;
      // ecpm usually use network currency
      if (this.props.metricType === METRIC.ECPM_RATIO) {
        selectedCurrency = currency;
      }

      this.setState({ twdConvertRate, currency, selectedCurrency }, () => {
        this.handleGetData();
      });
    } catch (e) {
      this.setState({ errmsg: e.message, isLoading: false });
    }
  }

  handleDateRangeFilterChanged({ startDate, endDate }) {
    this.setState(
      {
        startDate: moment(startDate).format("YYYY-MM-DD"),
        endDate: moment(endDate).format("YYYY-MM-DD"),
        originData: null,
      },
      () => {
        this.handleGetData();
      },
    );
  }

  handleMetricTypeChange(metricType) {
    let { selectedCurrency } = this.state;
    // ecpm usually use network currency
    if (metricType === METRIC.ECPM_RATIO) {
      selectedCurrency = this.state.currency;
    }
    this.setState({ metricType, selectedCurrency }, () => {
      this.handleGetData();
    });
  }

  handleCurrencyChange(currency) {
    this.setState({ selectedCurrency: currency }, () => {
      this.handleChartOption();
    });
  }

  async handleGetData() {
    if (this.state.originData && this.state.originData[this.state.metricType]) {
      this.handleChartOption();
      return;
    }

    this.setState({ isLoading: true, options: null, errmsg: "" });
    try {
      const result = await AnatomyAPI.getUnitTrendByMetric({
        networkId: this.props.networkId,
        extGamUnitId: this.props.extUnitId,
        startDate: this.state.startDate,
        endDate: this.state.endDate,
        metricKey: this.state.metricType,
      });

      const parsedData = this._parsedData(result, this.state.metricType);

      this.setState(
        {
          originData: {
            ...(this.state.originData || {}),
            ...parsedData,
          },
        },
        () => {
          this.handleChartOption();
        },
      );
    } catch (e) {
      this.setState({ errmsg: e.message });
    }

    this.setState({ isLoading: false });
  }

  handleChartOption() {
    const { metricType, originData, selectedCurrency, twdConvertRate } =
      this.state;

    const isPercentage = metricType !== METRIC.REQUEST_TREND;
    const isBvsO = [
      METRIC.ECPM_RATIO,
      METRIC.STR_RATIO,
      METRIC.BO_IVT_RATIO,
    ].includes(metricType);

    const data = _.cloneDeep(originData[metricType]);
    if (!data) return;

    const dataSeries = _(data)
      .map((item) => {
        const y = isPercentage ? item.value * 100 : item.value;
        return {
          ...item,
          x: new Date(item.date).getTime(),
          y,
          color: y < HELPER_LINE && isBvsO ? "#ff9696" : undefined,
        };
      })
      .sortBy("x")
      .value();

    const dataMin = _.get(_.minBy(dataSeries, "y"), "y", 0);
    const avgValue = _.round(
      _.meanBy(dataSeries, (d) => d.y),
      2,
    );

    const options = {
      chart: {
        height: 320,
        zoomType: "x",
        events: {
          load: function () {
            addChartBgSection(this);
          },
        },
        marginRight: 40,
        marginLeft: 60,
      },
      title: {
        text: "",
      },
      tooltip: {
        useHTML: true,
        padding: 1,
        formatter: function () {
          const rate = selectedCurrency === "TWD" ? twdConvertRate : 1;
          const currencySymbol = getCurrencySymbol(selectedCurrency);

          let additionHint = "";
          let tooltipFormat = "0.00";
          switch (metricType) {
            case METRIC.COST_TREND:
              additionHint = `<br/>Cost: <b>${currencySymbol}${numeral(
                this.point.cost * rate,
              ).format("0,0.0")}</b><br/>Revenue: <b>${currencySymbol}${numeral(
                this.point.revenue * rate,
              ).format("0,0.0")}</b>`;
              tooltipFormat = "0";
              break;
            case METRIC.ECPM_RATIO:
              additionHint = createBOTooltipHtml(
                [
                  {
                    label: "eCPM",
                    key: ["bEcpm", "oEcpm"],
                    symbol: currencySymbol,
                    format: "0,0.00",
                  },
                  {
                    label: "Revenue",
                    key: ["bRev", "oRev"],
                    symbol: currencySymbol,
                    format: "0,0.00",
                  },
                  {
                    label: "Impression",
                    key: ["bImp", "oImp"],
                    format: "0,0",
                  },
                ].map((item) => ({
                  ...item,
                  valList: item.key.map(
                    (k) => (this.point[k] || 0) * (item.symbol ? rate : 1),
                  ),
                })),
              );
              tooltipFormat = "0";
              break;
            case METRIC.STR_RATIO:
              additionHint = createBOTooltipHtml(
                [
                  { label: "STR Ratio", key: ["bStr", "oStr"], format: "0%" },
                  { label: "Impression", key: ["bImp", "oImp"], format: "0,0" },
                  { label: "Request", key: ["bReq", "oReq"], format: "0,0" },
                ].map((item) => ({
                  ...item,
                  valList: item.key.map((k) => this.point[k] || 0),
                })),
              );
              tooltipFormat = "0";
              break;
            case METRIC.BO_IVT_RATIO:
              additionHint = createBOTooltipHtml(
                [
                  {
                    label: "IVT Ratio",
                    key: ["bIvt", "oIvt"],
                    format: "0.00%",
                  },
                  { label: "Imp.", key: ["bImp", "oImp"], format: "0,0" },
                  {
                    label: "Unfiltered Imp.",
                    key: ["bUnfiltered", "oUnfiltered"],
                    format: "0,0",
                  },
                ].map((item) => ({
                  ...item,
                  valList: item.key.map((k) => this.point[k] || 0),
                })),
              );
              tooltipFormat = "0";
              break;
            case METRIC.REQUEST_TREND:
              tooltipFormat = "0,0";
              break;
            default:
              break;
          }
          return `<div style="background: white; padding: 8px;"><b>${moment(
            this.x,
          ).format("YYYY-MM-DD ddd")}</b><br/><b>${numeral(this.y).format(
            tooltipFormat,
          )}${isPercentage ? "%" : ""}</b>${additionHint}</div>`;
        },
      },
      yAxis: {
        title: {
          text: "%",
        },
        min: dataMin < 0 ? dataMin : 0,
        plotBands: this._createYAxisPlotBands(),
        plotLines: [
          {
            color: "#718096",
            width: 1,
            value: avgValue,
            dashStyle: "Dash",
            label: {
              useHTML: true,
              text:
                "Avg.<br/>" +
                (isPercentage
                  ? avgValue.toFixed(0) + "%"
                  : numeral(avgValue).format("0,0")),
              align: "right",
              x: 30,
              style: { color: "#718096", fontWeight: "bold" },
            },
          },
          ...(isBvsO
            ? [
                {
                  color: "#f87171",
                  width: 1,
                  value: HELPER_LINE,
                  label: {
                    useHTML: true,
                    text: `<span style="color:#f87171;font-weight:bold;">${HELPER_LINE}</span>`,
                    align: "left",
                    x: -36,
                    y: 2,
                    style: { color: "#718096", fontWeight: "bold" },
                  },
                },
              ]
            : []),
        ],
        labels: {
          useHTML: true,
          formatter: function () {
            return isBvsO && this.value === HELPER_LINE ? "" : this.value;
          },
        },
      },

      series: [
        {
          name: `${METRIC_TITLE[metricType].subtitle} ${METRIC_TITLE[metricType].title}`,
          type: "line",
          data: dataSeries,
        },
      ],
    };

    this.setState({ options });
  }

  _createYAxisPlotBands() {
    return [
      {
        from: -9999999,
        to: 0,
        color: "#f4e6e6",
      },
    ];
  }

  _parsedData(data, metric) {
    const result = {};

    const setMetricData = (metric, data) => {
      result[metric] = _.map(data, (d) => {
        let additionalData = {};
        switch (metric) {
          case METRIC.COST_TREND:
            additionalData = {
              cost: normalizeRevenue(d.cost || 0),
              revenue: normalizeRevenue(
                (d.totalRevenue || 0) - (d.originalRevenue || 0),
              ),
            };
            break;
          case METRIC.ECPM_RATIO:
            additionalData = {
              bEcpm: d.b_ecpm || 0,
              oEcpm: d.o_ecpm || 0,
              bImp: d.b_imp || 0,
              oImp: d.o_imp || 0,
              bRev: d.b_rev || 0,
              oRev: d.o_rev || 0,
            };
            break;
          case METRIC.STR_RATIO:
            additionalData = {
              bStr: d.b_str || 0,
              oStr: d.o_str || 0,
              bImp: d.b_imp || 0,
              oImp: d.o_imp || 0,
              bReq: d.b_req || 0,
              oReq: d.o_req || 0,
            };
            break;
          case METRIC.BO_IVT_RATIO:
            const bIvt = _.round(_.get(d, ["ivt_ratio", "b"], 0), 4);
            const oIvt = _.round(_.get(d, ["ivt_ratio", "o"], 0), 4);
            const bImp = _.get(d, ["ad_server_impression", "b"], 0);
            const oImp = _.get(d, ["ad_server_impression", "o"], 0);
            const bUnfiltered = _.get(
              d,
              ["ad_server_unfiltered_impression", "b"],
              0,
            );
            const oUnfiltered = _.get(
              d,
              ["ad_server_unfiltered_impression", "o"],
              0,
            );

            additionalData = {
              bIvt,
              oIvt,
              bImp,
              oImp,
              bUnfiltered,
              oUnfiltered,
              boIvtRatio: _.round(bIvt === 0 ? 0 : oIvt / bIvt, 4),
            };
            break;
          default:
            break;
        }

        return {
          date: d.date,
          value:
            d[METRIC_KEY[metric]] || additionalData[METRIC_KEY[metric]] || 0,
          ...additionalData,
        };
      });
    };

    if (
      [METRIC.PSCORE_TREND, METRIC.DSCORE_TREND, METRIC.UPRKEY_TREND].includes(
        metric,
      )
    ) {
      _.forEach(
        [METRIC.PSCORE_TREND, METRIC.DSCORE_TREND, METRIC.UPRKEY_TREND],
        (m) => {
          setMetricData(m, data);
        },
      );
    } else {
      setMetricData(metric, data);
    }

    return result;
  }

  render() {
    const { unitId, unitName, networkName, networkId } = this.props;

    const {
      startDate,
      endDate,
      metricType,
      options,
      isLoading,
      errmsg,
      selectedCurrency,
      currency,
    } = this.state;

    return (
      <ModalWrapper
        isOpen={true}
        handleClose={this.props.handleClose}
        showCloseFooter={true}
        customOverlayStyle={{ zIndex: Z_INDEX.MODAL_INDEX }}
        title={{
          label: "Metric Trend Charts ",
          highlight: `${unitId} - ${unitName}`,
        }}
      >
        {networkName && networkId && (
          <>
            <div className="font-semibold text-gray-600">Network:</div>
            <div className="mb-2 flex">
              <div className="mr-1 font-semibold text-gray-800">
                {networkId}
              </div>
              <div className="truncate mr-1 text-gray-800" title={networkName}>
                {networkName}
              </div>
            </div>
          </>
        )}
        <div className="font-semibold text-gray-600">Date Range:</div>
        <div className="mb-2">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            format="YYYY-MM-DD ddd"
            handleDateRangeChanged={this.handleDateRangeFilterChanged}
            dateRanges={[
              DATE_RANGE_TYPE.THIS_WEEK,
              DATE_RANGE_TYPE.LAST_WEEK,
              DATE_RANGE_TYPE.LAST_2_WEEKS,
              DATE_RANGE_TYPE.THIS_MONTH,
              DATE_RANGE_TYPE.LAST_MONTH,
              DATE_RANGE_TYPE.LAST_MONTH_TO_TODAY,
            ]}
          ></DateRangePicker>
        </div>
        <div className="font-semibold text-gray-600">Metric Type:</div>
        <div className="border-b mb-2 flex flex-wrap gap-2 pb-2">
          {_.map(_.values(METRIC), (metric) => {
            return (
              <button
                className={`rounded mr-1 px-4 py-1 font-semibold text-gray-700 ${
                  metricType === metric
                    ? "bg-blue-200"
                    : "bg-gray-200 hover:bg-gray-300 hover:text-gray-800"
                }`}
                key={metric}
                onClick={() => this.handleMetricTypeChange(metric)}
              >
                {METRIC_TITLE[metric].title}
              </button>
            );
          })}
        </div>
        <div className="flex justify-end">
          {[METRIC.COST_TREND, METRIC.ECPM_RATIO].includes(metricType) &&
            currency !== "" && (
              <SwitchTabs
                tabs={_.uniq([currency, "TWD"]).map((d) => ({
                  value: d,
                  label: d,
                }))}
                selectedTabKey={selectedCurrency}
                handleChange={(val) => this.handleCurrencyChange(val)}
              ></SwitchTabs>
            )}
        </div>
        <div className="w-full">
          {isLoading && <LoadingUI></LoadingUI>}
          {errmsg !== "" && <div className="text-red-800">{errmsg}</div>}
          {options && (
            <HighchartWrapper
              options={options}
              key={`${metricType}-${selectedCurrency}`}
            ></HighchartWrapper>
          )}
        </div>
      </ModalWrapper>
    );
  }
}

export default UnitMetricTrendModal;
